import { useState } from 'react';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { IoClose, IoLogoWhatsapp, IoCheckmarkCircle, IoAlertCircle } from 'react-icons/io5';
import OTPInput, { ResendOTP } from 'otp-input-react';
import { formatPhoneNumber, formatPhoneNumberIntl, isValidPhoneNumber, isPossiblePhoneNumber } from 'react-phone-number-input';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import WhatsappOTPService from '@Helpers/URLHelpers/WeboutService/WhatsappOTPService';
import ErrorHandler from '@Root/ErrorHandler';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import './EnterContactNumberForm.scss';

export default function EnterContactNumberForm(props) {
    const { setActiveUser } = useReduxActiveUser();
    const [cursor, setCursor] = useState('auto');
    const [number, setNumber] = useState(null);
    const [, setIsPossibleNumber] = useState(false);
    const [isValidNumber, setIsValidNumber] = useState(false);
    const [, setIsNationalNumber] = useState(false);
    const [, setIsInternationalNumber] = useState(false);
    const [otpSent, setOTPSent] = useState(false);
    const [otp, setOtp] = useState(null);
    const [, setOTPSentError] = useState(false);
    const [userExists, setUserExists] = useState(false);
    const [disableVerifyButton, setDisableVerifyButton] = useState(false);
    const [, setIsVerifyTimer] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [, setStateSeconds] = useState(0);
    
    const userInfoService = new UserInfoService();
    const whatsappOTPService = new WhatsappOTPService();
    const errorHandler = new ErrorHandler();
    const activeUserId = localStorage.getItem('activeUserId');
    let seconds = 0;
    let timer = 0;
    const className = 'enterContactNumberForm';

    const onPhoneNumberChange = async(newNumber) => {
        let newNumberWithPlus = '+' + newNumber;
        let isPossibleNumberValue = newNumberWithPlus && isPossiblePhoneNumber(newNumberWithPlus) ? true : false;
        let isValidNumberValue = newNumberWithPlus && isValidPhoneNumber(newNumberWithPlus) ? true : false;
        setNumber(newNumber);
        setIsPossibleNumber(isPossibleNumberValue);
        setIsValidNumber(isValidNumberValue);
        setIsNationalNumber(newNumberWithPlus && formatPhoneNumber(newNumber));
        setIsInternationalNumber(newNumberWithPlus && formatPhoneNumberIntl(newNumberWithPlus));
        setUserExists(false);
    }

    const handleSendOTPClick = async() => {
        try {
            setCursor('wait');
            let numberValue = number;
            if(number.indexOf('+') != -1) {
                numberValue = numberValue.slice(1);
            }
            let existingUser = await userInfoService.userExistsByContactNumber(numberValue);
            existingUser = JSON.parse(existingUser.body);
            setUserExists(existingUser);
            if(!existingUser) {
                let numberMap = {
                    'number': numberValue
                }
                let sendOtp = await whatsappOTPService.sendOTPVerification(numberMap);
                sendOtp = JSON.parse(sendOtp.body);
                if(sendOtp) {
                    setOTPSent(true);
                    setDisableVerifyButton(true);
                    seconds = 30;
                    setStateSeconds(30);
                    setIsVerifyTimer(true);
                    startTimer();
                    SnackbarManager.success('Verification code sent to your WhatsApp');
                } else {
                    setOTPSentError(true);
                    SnackbarManager.error('Failed to send verification code. Please try again.');
                }
            } else {
                setUserExists(true);
            }
        } catch (err) {
            errorHandler.handleError(err);
            SnackbarManager.error('An error occurred. Please try again.');
        } finally {
            setCursor('auto');
        }
    }

    const startTimer = () => {
        if (timer === 0) {
            timer = setInterval(countDown, 1000);
        }
    }

    const countDown = () => {
        if(seconds > 0) {
            seconds = seconds - 1;
            setStateSeconds(seconds);
            setRefresh(!refresh);
        } else {
            clearInterval(timer);
            timer = 0;
            setRefresh(!refresh);
            setIsVerifyTimer(false);
            setDisableVerifyButton(false);
        }
    }

    const renderButton = (buttonProps) => {
        return (
            <button 
                {...buttonProps} 
                className={className + '__resend-btn'}
            >
                Resend Code
            </button>
        );
    };

    const renderTime = (remainingTime) => {
        return (
            <div className={className + '__timer'}>
                {remainingTime > 0 ? (
                    <span>Resend available in {remainingTime}s</span>
                ) : (
                    <span>Didn't receive the code?</span>
                )}
            </div>
        );
    };

    const verifyOTP = async() => {
        try {
            setCursor('wait');
            let numberValue = number;
            if(number.indexOf('+') != -1) {
                numberValue = numberValue.slice(1);
            }
            let verifyOTPMap = {
                'type': 'verify',
                'userId': activeUserId,
                'userName': JSON.parse(localStorage.activeUser).userName,
                'contactNumber': numberValue,
                'verificationCode': otp
            }
            let contactNumberUpdated = await whatsappOTPService.verifyAndSave(verifyOTPMap);
            contactNumberUpdated = JSON.parse(contactNumberUpdated.body);
            if(contactNumberUpdated) {
                let activeUserObj = await userInfoService.getUserById(activeUserId);
                activeUserObj = JSON.parse(activeUserObj.body);
                localStorage.setItem('activeUser', JSON.stringify(activeUserObj));
                
                // ✅ Update Redux state BEFORE reloading
                setActiveUser(activeUserObj);
                
                // Show success message
                SnackbarManager.success('WhatsApp number verified and connected successfully!');
                
                // Reload profile data if callback provided
                if(props.reloadData) {
                    await props.reloadData();
                }
                
                // Close the form
                props.closePopup();
            } else {
                SnackbarManager.error('Invalid verification code. Please try again.');
            }
        } catch(err) {
            errorHandler.handleError(err);
            SnackbarManager.error('Verification failed. Please check your code and try again.');
        } finally {
            setCursor('auto');
        }
    }

    const steps = ['Enter Number', 'Verify OTP'];

    return (
        <Modal
            open={props.open}
            onClose={props.closePopup}
            style={{ cursor: cursor }}
            className={className}
        >
            <div className={className + '__container'}>
                {/* Header */}
                <div className={className + '__header'}>
                    <div className={className + '__header__icon'}>
                        <IoLogoWhatsapp />
                    </div>
                    <div className={className + '__header__content'}>
                        <h2 className={className + '__header__title'}>Connect WhatsApp</h2>
                        <p className={className + '__header__subtitle'}>
                            Get updates, notifications, and reports directly on WhatsApp
                        </p>
                    </div>
                    <IconButton 
                        onClick={props.closePopup}
                        className={className + '__header__close'}
                    >
                        <IoClose />
                    </IconButton>
                </div>

                {/* Stepper */}
                <div className={className + '__stepper'}>
                    <Stepper activeStep={otpSent ? 1 : 0} alternativeLabel>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </div>

                {/* Content */}
                <div className={className + '__content'}>
                    {!otpSent ? (
                        // Phone Number Step
                        <div className={className + '__phone-step'}>
                            <div className={className + '__info-box'}>
                                <IoCheckmarkCircle className={className + '__info-box__icon'} />
                                <p>
                                    Enter your phone number to receive a one-time verification code via WhatsApp.
                                </p>
                            </div>

                            <div className={className + '__phone-input'}>
                                <label className={className + '__label'}>Phone Number</label>
                                <PhoneInput
                                    country='in'
                                    placeholder='Enter phone number'
                                    value={number}
                                    disabled={disableVerifyButton}
                                    onChange={(newNumber) => onPhoneNumberChange(newNumber)}
                                    containerClass={className + '__phone-container'}
                                    inputClass={className + '__phone-field'}
                                    buttonClass={className + '__phone-dropdown'}
                                />
                                
                                {userExists && (
                                    <div className={className + '__error'}>
                                        <IoAlertCircle />
                                        <span>This number is already registered with another account</span>
                                    </div>
                                )}
                                
                                {(!isValidNumber && number !== null) && !userExists && (
                                    <div className={className + '__error'}>
                                        <IoAlertCircle />
                                        <span>Please enter a valid phone number</span>
                                    </div>
                                )}
                            </div>

                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                onClick={handleSendOTPClick}
                                disabled={!isValidNumber || disableVerifyButton || cursor === 'wait'}
                                className={className + '__submit-btn'}
                            >
                                {cursor === 'wait' ? 'Sending...' : 'Send Verification Code'}
                            </Button>
                        </div>
                    ) : (
                        // OTP Verification Step
                        <div className={className + '__otp-step'}>
                            <div className={className + '__info-box'}>
                                <IoCheckmarkCircle className={className + '__info-box__icon'} />
                                <p>
                                    We've sent a 6-digit verification code to <strong>+{number}</strong>
                                </p>
                            </div>

                            <div className={className + '__otp-input'}>
                                <label className={className + '__label'}>Verification Code</label>
                                <OTPInput 
                                    value={otp} 
                                    onChange={setOtp} 
                                    autoFocus 
                                    OTPLength={6} 
                                    otpType='number' 
                                    disabled={!otpSent || !isValidNumber}
                                    className={className + '__otp-fields'}
                                />
                                
                                <div className={className + '__resend-section'}>
                                    <ResendOTP 
                                        maxTime={30} 
                                        onResendClick={handleSendOTPClick} 
                                        renderButton={renderButton} 
                                        renderTime={renderTime} 
                                    />
                                </div>

                                <p className={className + '__otp-note'}>
                                    The code is valid for 15 minutes. If you don't receive it, please verify your number and try again.
                                </p>
                            </div>

                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                onClick={verifyOTP}
                                disabled={!otpSent || (otp?.length !== 6) || cursor === 'wait'}
                                className={className + '__submit-btn'}
                            >
                                {cursor === 'wait' ? 'Verifying...' : 'Verify & Connect'}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}
